import { router } from './services/router.js';
import { resolveReturnUrl } from './services/return-url.js';
import { isInstalledPwa, requestOfflineEngineCaching } from './services/pwa.js';
import { initInstallPrompt } from './services/install-prompt.js';

/**
 * App-wide client behaviors that aren't tied to a single component:
 *  - After a successful sign-in/registration (any `gc-auth-success` bubbling to the document),
 *    navigate to the page the user originally requested (`?returnUrl=` set when an anonymous
 *    visitor was bounced to /login from a protected page, e.g. a shared game link), or the lobby.
 *  - Register the app-shell Service Worker (served at the root so it can control the whole origin),
 *    enabling offline loading. Registration is best-effort and never blocks the app.
 *  - For installed PWA users, ask the Service Worker to precache the heavy offline engine (WASM +
 *    ONNX) so offline play works even before visiting /offline, and stays current after updates.
 *  - Capture the PWA install prompt early so the home page's "Install as app" call-out can offer it.
 */
export function bootstrapApp(): void {
  document.addEventListener('gc-auth-success', () => {
    router.navigate(resolveReturnUrl(window.location.search));
  });

  initInstallPrompt();
  registerServiceWorker();
}

function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  window.addEventListener('load', () => {
    // Served from the root path (see the /sw.js server endpoint) so its scope is the whole origin.
    navigator.serviceWorker
      .register('/sw.js')
      .then(() => ensureOfflineEngineCached())
      .catch(() => {
        // Offline support is a progressive enhancement; ignore registration failures.
      });

    // After an updated worker takes control (a new precache version activated), re-request the
    // offline engine so an installed PWA keeps its offline assets current.
    navigator.serviceWorker.addEventListener('controllerchange', () => ensureOfflineEngineCached());
  });
}

/**
 * Asks the active Service Worker to precache (and keep current) the offline engine tier, but only for
 * installed PWA users — casual web visitors shouldn't be forced to download the multi-megabyte
 * WASM/ONNX bundle. Re-running it after each SW update (controllerchange) keeps an installed app's
 * offline play always available and up to date. A normal browser tab instead caches the engine
 * on-demand the first time it opens /offline (see gc-game-table).
 */
function ensureOfflineEngineCached(): void {
  if (!isInstalledPwa()) {
    return;
  }
  requestOfflineEngineCaching();
}
